/**
 * Every tool name the API can send, with a human label.
 *
 * EXHAUSTIVE, not "the ones we happened to see". The render falls back to
 * `data.tool`, so a missing entry puts a raw snake_case identifier on screen
 * beside a spinner — `create_prejob_meeting` did exactly that, and a label map
 * that echoes its key is how a display gap gets reported as a data bug.
 *
 * The 35 names here were taken from fieldiq-api `origin/main`
 * src/services/ai-agent.service.ts on 2026-09-11, cross-checked two ways: the
 * `name:` field of every ToolDef, and every `case` in executeTool's switch.
 * Both extractions returned the same 35 with no set difference.
 *
 * Seven of them draft before they write (create_jsa, create_prejob_meeting,
 * approve_ticket, generate_invoice, record_payment, schedule_job, create_job).
 * These labels still read as the finished action for all seven; a draft-aware
 * label waits on the API emitting an explicit flag on tool_result, because
 * string-matching the result prose to guess is a coupling worth avoiding.
 */
export const TOOL_LABELS: Record<string, string> = {
  search_jobs: 'Searching jobs',
  get_job_details: 'Loading job details',
  search_tickets: 'Searching tickets',
  get_ticket_details: 'Loading ticket details',
  search_invoices: 'Searching invoices',
  get_invoice_details: 'Loading invoice details',
  get_customer_balance: 'Checking customer balance',
  search_customers: 'Searching customers',
  search_equipment: 'Searching equipment',
  get_schedule: 'Loading schedule',
  search_employees: 'Searching employees',
  get_revenue_summary: 'Analyzing revenue',
  get_safety_summary: 'Checking safety records',
  approve_ticket: 'Approving ticket',
  generate_invoice: 'Generating invoice',
  record_payment: 'Recording payment',
  locate_equipment: 'Locating equipment',
  get_crew_availability: 'Checking crew availability',
  get_inspection_alerts: 'Checking inspections',
  schedule_job: 'Scheduling job',
  update_equipment_status: 'Updating equipment',
  suggest_pay_assignments: 'Analyzing pay',
  create_job: 'Creating new job',
  recall_memories: 'Checking what I know',
  save_memory: 'Learning for next time',
  get_rig_history: 'Analyzing rig history',
  get_customer_insights: 'Reviewing customer intel',
  get_morning_briefing: 'Reading the morning brief',
  get_safety_context: 'Checking safety context',
  create_customer: 'Creating customer',
  create_jsa: 'Creating JSA',
  create_prejob_meeting: 'Creating pre-job meeting',
  report_incident: 'Reporting incident',
  report_near_miss: 'Reporting near miss',
  submit_equipment_inspection: 'Submitting equipment inspection',
};

/**
 * The label for a tool, or the raw name when there is none.
 *
 * The fallback is kept deliberately and is NOT the map's safety net — it is the
 * thing the map exists to prevent reaching. A caller that hits it is showing a
 * user `submit_equipment_inspection`, which is the same class of bug as a role
 * column echoing its enum. `agentToolLabelIsKnown` is what a test asserts on.
 */
export function agentToolLabel(toolName: string): string {
  return TOOL_LABELS[toolName] ?? toolName;
}

export function agentToolLabelIsKnown(toolName: string): boolean {
  return Object.prototype.hasOwnProperty.call(TOOL_LABELS, toolName);
}
